/**
 * Global state store using Zustand.
 */

import { create } from 'zustand'
import { api } from './api'
import { wipeAllPHI } from './durableAudio'

interface Doctor {
  id: string
  email: string
  name: string
  specialty: string
  subspecialty?: string
  settings?: Record<string, any>
  onboarding_complete?: boolean
}

interface AppState {
  // Auth
  doctor: Doctor | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (data: { email: string; password: string; name: string; specialty: string; practice_size?: string; current_emr?: string; agree_terms: boolean; agree_privacy: boolean; agree_baa: boolean }) => Promise<void>
  loginWithGoogle: (credential: string) => Promise<void>
  handleCognitoCallback: (code: string) => Promise<void>
  logout: () => void
  logoutAllSessions: () => Promise<void>
  checkAuth: () => Promise<void>
  updateDoctor: (doctor: Doctor) => void
  setDoctor: (doctor: Doctor) => void
  markQuickstartSeen: () => void
  setAuthFromToken: (token: string, doctor?: Doctor) => void
}

export const useStore = create<AppState>((set, get) => ({
  doctor: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    const res = await api.login(email, password)
    api.setToken(res.access_token)
    set({ doctor: res.doctor, isAuthenticated: true })
  },

  register: async (data) => {
    const res = await api.register(data)
    api.setToken(res.access_token)
    set({ doctor: res.doctor, isAuthenticated: true })
  },

  loginWithGoogle: async (credential: string) => {
    const res = await api.googleLogin(credential)
    api.setToken(res.access_token)
    set({ doctor: res.doctor, isAuthenticated: true })
  },

  handleCognitoCallback: async (code: string) => {
    const redirectUri = `${window.location.origin}/auth/callback`
    const res = await api.cognitoCallback(code, redirectUri)
    api.setToken(res.access_token)
    set({ doctor: res.doctor, isAuthenticated: true })
  },

  logout: () => {
    // Clear locally-cached PHI (audio IndexedDB + offline keys) before dropping
    // the token — critical on shared/public computers.
    wipeAllPHI().catch(() => {})
    api.setToken(null)
    set({ doctor: null, isAuthenticated: false })
  },

  logoutAllSessions: async () => {
    try { await api.logoutAll() } catch { /* proceed to local logout regardless */ }
    wipeAllPHI().catch(() => {})
    api.setToken(null)
    set({ doctor: null, isAuthenticated: false })
  },

  checkAuth: async () => {
    const token = api.getToken()
    if (!token) {
      set({ isLoading: false })
      return
    }
    try {
      const doctor = await api.me()
      set({ doctor, isAuthenticated: true, isLoading: false })
      // Self-heal: if this browser already saw the quickstart but the server
      // flag never persisted (dropped fire-and-forget PATCH, OAuth bounce,
      // token refresh), re-assert it so the tour stops recurring everywhere.
      try {
        const localSeen = localStorage.getItem('scribey_quickstart_seen') === '1'
        if (localSeen && doctor && !doctor.settings?.quickstart_seen) {
          set({ doctor: { ...doctor, settings: { ...(doctor.settings || {}), quickstart_seen: true } } })
          api.updatePreferences({ settings: { quickstart_seen: true } }).catch(() => {})
        }
      } catch {}
    } catch {
      api.setToken(null)
      set({ isLoading: false })
    }
  },

  setAuthFromToken: (token: string, doctor?: Doctor) => {
    api.setToken(token)
    if (doctor) set({ doctor, isAuthenticated: true })
    else { set({ isAuthenticated: true }); get().checkAuth() }
  },

  updateDoctor: (doctor: Doctor) => {
    set({ doctor })
  },

  setDoctor: (doctor: Doctor) => {
    set({ doctor })
  },

  markQuickstartSeen: () => {
    // Set localStorage as a fast local cache / legacy flag
    try { localStorage.setItem('scribey_quickstart_seen', '1') } catch {}
    // Update doctor in store immediately so redirect logic stops matching
    const doctor = get().doctor
    if (doctor) {
      set({ doctor: { ...doctor, settings: { ...(doctor.settings || {}), quickstart_seen: true } } })
    }
    // Reliable server-side persistence: retry once on failure so a single
    // transient error doesn't leave the flag unset (the recurrence bug).
    const persistSeen = (attempt = 0) => {
      api.updatePreferences({ settings: { quickstart_seen: true } }).catch(() => {
        if (attempt < 2) setTimeout(() => persistSeen(attempt + 1), 1500 * (attempt + 1))
      })
    }
    persistSeen()
  },
}))
